import { Vector3, BufferGeometry, Raycaster } from "three";
import * as THREE from "three";
import { DragControls } from "three/addons/controls/DragControls.js";
import { POLYGONS, VARS, VERTICES, LABELS } from "../Global/variables";
import * as Details from "./AreaPitchAzimu";
import { sceneManager } from "../SetUps/SceneManager";
import { updateLabelPositions, formatFeetAndInches } from "./Labels";
import { createSegmentPlane } from "../Global/functions";
import { updateSegmentWireframe } from "../Global/fatLines";
import { pushUndoAction } from "../Global/UndoManager"; // ⬅️ undo/redo
import { isIrradianceInvalidationSuppressed } from "../Shading/IrradianceEvents";
import {
  addSetbackToEdge,
  updateWireframe as updateSetbackWireframe,
} from "./SetBacks/Setbacks";

// Track which vertex is being dragged
let vertexIndex;

// Track the wireframe and its base color during vertex drag
let currentWireframe = null;
let wireframeBaseColor = null;

// Snap distance for 90° vertex drag (very tight, in local/world units of the polygon)
const VERTEX_90_SNAP_DIST = 0.005;

// How close the angle must be to 90° (in degrees) to allow snapping
const ANGLE_SNAP_ENTER_TOL_DEG = 1.0;

// How close to 90° to consider it "highlighted" (green) after snapping
const ANGLE_HIGHLIGHT_TOL_DEG = 0.5;

// DOM element for live angle display while dragging
let angleLabelDiv = null;

// -------------------------------------------------------
// Global drag mode and states
// -------------------------------------------------------

let currentDragMode = null; // null | "vertex" | "translate" | "rotate"

// drag controls for segment vertices, edge-translation handles, and rotation handles
let edgeTranslationDragControls = null;
let rotationDragControls = null;

// handles for translating (edge midpoints) and rotating (offset from vertices)
let edgeHandles = [];
let rotationHandles = [];
let edgePickers = [];
let edgeHighlightLine = null;
let edgeDragControls = null;
let edgeDragState = null;
const vertexRaycaster = new Raycaster();

// state for translation + rotation
// translateState: { segmentIndex, plane, originalPoints, handleStart, moved }
let translateState = null;

// rotationState: { segmentIndex, plane, center, originalPoints, baseDir, moved }
let rotationState = null;

// vertex drag undo state: { segmentIndex, originalPoints, didMove }
let vertexDragState = null;

// remember current polygon details callback so we can update UI from any transform
let currentSetPolygonDetails = null;

// Keep rotation handles near corners with a slight outward offset and tiny plane lift.
const ROTATION_HANDLE_NORMAL_LIFT = 0.01;
const ROTATION_HANDLE_CORNER_OFFSET = 0.2;

const HANDLE_COLORS = {
  translate: 0xff6666,
  translateHover: 0xffb3b3,
  rotate: 0xEDE24E,
  rotateHover: 0xFFF27A,
};

const EDGE_HANDLE_STYLE = {
  lineOpacity: 0.75,
  lineHoverOpacity: 0.65,
  blockWidth: 0.12,
  blockDepth: 0.03,
  blockLengthRatio: 0.35,
  blockMin: 0.18,
  blockMax: 0.6,
  blockOpacity: 0.7,
};

const ROTATION_HANDLE_STYLE = {
  innerRadius: 0.18,
  outerRadius: 0.225,
  arc: Math.PI * 0.78,
  arrowRadius: -0.03,
  arrowSize: 0.112,
};

// -------------------------------------------------------
// Angle label helpers (existing)
// -------------------------------------------------------

function ensureAngleLabelDiv() {
  if (!angleLabelDiv) {
    angleLabelDiv = document.createElement("div");
    angleLabelDiv.style.position = "absolute";
    angleLabelDiv.style.pointerEvents = "none";
    angleLabelDiv.style.padding = "2px 4px";
    angleLabelDiv.style.borderRadius = "4px";
    angleLabelDiv.style.fontSize = "11px";
    angleLabelDiv.style.fontFamily = "sans-serif";
    angleLabelDiv.style.background = "rgba(0,0,0,0.7)";
    angleLabelDiv.style.color = "#fff";
    angleLabelDiv.style.zIndex = "9999";
    angleLabelDiv.style.display = "none";
    document.body.appendChild(angleLabelDiv);
  }
}

function updateAngleLabel(angleDeg, worldPos) {
  ensureAngleLabelDiv();

  const camera = sceneManager.camera;
  const renderer = sceneManager.renderer;

  const proj = worldPos.clone().project(camera);
  const width = renderer.domElement.clientWidth;
  const height = renderer.domElement.clientHeight;

  const x = (proj.x * 0.5 + 0.5) * width;
  const y = (-proj.y * 0.5 + 0.5) * height;

  angleLabelDiv.textContent = `${angleDeg.toFixed(1)}°`;
  angleLabelDiv.style.left = `${x + 6}px`;
  angleLabelDiv.style.top = `${y + 6}px`;
  angleLabelDiv.style.display = "block";
}

function hideAngleLabel() {
  if (angleLabelDiv) {
    angleLabelDiv.style.display = "none";
  }
}

function computeAngleDeg(A, V, B) {
  const v1 = A.clone().sub(V);
  const v2 = B.clone().sub(V);
  const len1 = v1.length();
  const len2 = v2.length();
  if (len1 < 1e-6 || len2 < 1e-6) return null;

  const dot = v1.dot(v2) / (len1 * len2);
  const clamped = Math.min(1, Math.max(-1, dot));
  return (Math.acos(clamped) * 180) / Math.PI;
}

/**
 * Given A, B, P, snap P to the "right-angle circle" (Thales) if close enough.
 */
function getVertexRightAngleSnap(A, B, P, maxSnapDist = VERTEX_90_SNAP_DIST) {
  const C = new Vector3().addVectors(A, B).multiplyScalar(0.5);
  const R = A.distanceTo(B) * 0.5;
  if (R <= 1e-6) return null;

  const dir = P.clone().sub(C);
  let len = dir.length();

  if (len < 1e-6) {
    dir.subVectors(B, A);
    len = dir.length();
    if (len < 1e-6) return null;
  }

  dir.multiplyScalar(1 / len);
  const snapped = C.clone().add(dir.multiplyScalar(R));

  const distToSnap = snapped.distanceTo(P);
  return distToSnap <= maxSnapDist ? snapped : null;
}

/**
 * Signed angle between two vectors around a normal.
 */
function signedAngleBetween(v1, v2, normal) {
  const v1n = v1.clone().normalize();
  const v2n = v2.clone().normalize();
  const dot = THREE.MathUtils.clamp(v1n.dot(v2n), -1, 1);
  const angle = Math.acos(dot);
  const cross = new Vector3().crossVectors(v1n, v2n);
  const sign = Math.sign(cross.dot(normal));
  return angle * sign;
}

// -------------------------------------------------------
// Segment helpers
// -------------------------------------------------------

function getPointsCenter(points) {
  const center = new Vector3();
  points.forEach((p) => center.add(p));
  center.divideScalar(points.length || 1);
  return center;
}

/**
 * Helper: get current world-space vertices of a segment from its edges.
 */
function getSegmentCurrentPoints(segmentIndex) {
  const segment = POLYGONS[segmentIndex];
  if (!segment || !segment.edges) return [];
  return segment.edges.map((e) => e.point1.clone());
}

/**
 * Notify app when a segment geometry changes (used to invalidate irradiance).
 */
function dispatchSegmentGeometryChanged(segmentIndex) {
  if (isIrradianceInvalidationSuppressed()) return;
  if (typeof window === "undefined") return;
  const segment = POLYGONS[segmentIndex];
  if (!segment?.id) return;
  window.dispatchEvent(
    new CustomEvent("SEGMENT_GEOMETRY_CHANGED", {
      detail: { segmentId: segment.id, segmentIndex },
    })
  );
}

function getPlaneNormal(plane) {
  if (plane?.isVector3 && plane.lengthSq() > 1e-6) {
    return plane.clone().normalize();
  }
  if (plane?.normal && plane.normal.lengthSq() > 1e-6) {
    return plane.normal.clone().normalize();
  }
  return new Vector3(0, 0, 1);
}

function getPlaneBasis(normal) {
  const n = normal.clone().normalize();
  let u = new Vector3(1, 0, 0);
  if (Math.abs(n.dot(u)) > 0.9) {
    u.set(0, 1, 0);
  }
  const v = new Vector3().crossVectors(n, u).normalize();
  const uOrtho = new Vector3().crossVectors(v, n).normalize();
  return { u: uOrtho, v, n };
}

function orientObjectToEdge(object, edgeDir, normal) {
  const x = edgeDir.clone().normalize();
  const z = normal.clone().normalize();
  let y = new Vector3().crossVectors(z, x);
  if (y.lengthSq() < 1e-6) {
    y = new Vector3(0, 1, 0);
  }
  y.normalize();
  const xOrtho = new Vector3().crossVectors(y, z).normalize();
  const matrix = new THREE.Matrix4().makeBasis(xOrtho, y, z);
  object.quaternion.setFromRotationMatrix(matrix);
}

function updateEdgeHandleVisual(handle, p1, p2, planeNormal) {
  if (!handle || !p1 || !p2) return;

  const edgeDir = p2.clone().sub(p1);
  const edgeLen = edgeDir.length();
  if (edgeLen < 1e-6) return;

  const mid = new Vector3().addVectors(p1, p2).multiplyScalar(0.5);
  handle.position.copy(mid);

  const normal = getPlaneNormal(planeNormal);
  orientObjectToEdge(handle, edgeDir, normal);

  const block = handle.userData.edgeBlock;
  const line = handle.userData.edgeLine;
  if (block) {
    const blockLength = THREE.MathUtils.clamp(
      edgeLen * EDGE_HANDLE_STYLE.blockLengthRatio,
      EDGE_HANDLE_STYLE.blockMin,
      EDGE_HANDLE_STYLE.blockMax
    );
    block.scale.set(
      blockLength,
      EDGE_HANDLE_STYLE.blockWidth,
      EDGE_HANDLE_STYLE.blockDepth
    );
  }

  if (line) {
    const lineLen = Math.max(edgeLen, EDGE_HANDLE_STYLE.blockMin);
    const lineGeometry = new BufferGeometry().setFromPoints([
      new Vector3(-lineLen * 0.5, 0, 0),
      new Vector3(lineLen * 0.5, 0, 0),
    ]);
    if (line.geometry) line.geometry.dispose();
    line.geometry = lineGeometry;
  }
}

function updateRotationHandleVisual(handle, center, corner, planeNormal) {
  if (!handle || !center || !corner) return;
  const normal = getPlaneNormal(planeNormal);
  const outward = corner.clone().sub(center);
  if (outward.lengthSq() > 1e-8) outward.normalize();
  else outward.set(1, 0, 0);
  const handlePos = corner
    .clone()
    .add(outward.clone().multiplyScalar(ROTATION_HANDLE_CORNER_OFFSET))
    .add(normal.clone().multiplyScalar(ROTATION_HANDLE_NORMAL_LIFT));

  handle.position.copy(handlePos);
  handle.userData.baseDir = corner.clone().sub(center);
  if (handle.userData.baseDir.lengthSq() < 1e-8) {
    handle.userData.baseDir.set(1, 0, 0);
  }

  const { u, v, n } = getPlaneBasis(normal);
  const angle = Math.atan2(handle.userData.baseDir.dot(v), handle.userData.baseDir.dot(u));
  const matrix = new THREE.Matrix4().makeBasis(u, v, n);
  handle.quaternion.setFromRotationMatrix(matrix);
  handle.rotateOnAxis(new Vector3(0, 0, 1), angle);
}

function setHandleHover(handle, isHover) {
  if (!handle) return;
  const target = isHover ? handle.userData.hoverColor : handle.userData.baseColor;
  if (handle.userData.rotateParts?.length) {
    handle.userData.rotateParts.forEach((part) => {
      if (part.material?.color) part.material.color.setHex(target);
      if (part.material && typeof part.material.opacity === "number") {
        const baseOpacity =
          typeof part.userData?.baseOpacity === "number" ? part.userData.baseOpacity : 0.9;
        part.material.opacity = isHover ? 1 : baseOpacity;
      }
    });
    return;
  }
  if (handle.userData.edgeBlock?.material?.color) {
    handle.userData.edgeBlock.material.color.setHex(target);
    handle.userData.edgeBlock.material.opacity = isHover ? 1 : EDGE_HANDLE_STYLE.blockOpacity;
  } else if (handle.material?.color) {
    handle.material.color.setHex(target);
    if (typeof handle.material.opacity === "number") {
      handle.material.opacity = isHover ? 1 : 0.9;
    }
  }
  if (handle.userData.edgeLine?.material) {
    handle.userData.edgeLine.material.color.setHex(target);
    handle.userData.edgeLine.material.opacity = isHover
      ? EDGE_HANDLE_STYLE.lineHoverOpacity
      : EDGE_HANDLE_STYLE.lineOpacity;
  }
}

function resolveHandleFromEvent(event) {
  const obj = event?.object;
  if (!obj) return null;
  if (obj.isGroup) return obj;
  if (obj.parent?.isGroup) return obj.parent;
  return obj;
}

function disposeObject3D(obj) {
  if (!obj) return;
  obj.userData.disposed = true;
  if (obj.parent) obj.parent.remove(obj);
  obj.traverse((child) => {
    if (child.geometry) child.geometry.dispose();
    if (child.material) {
      if (Array.isArray(child.material)) {
        child.material.forEach((mat) => mat.dispose());
      } else {
        child.material.dispose();
      }
    }
  });
}

function createEdgeHandle(position, segmentIndex, edgeIndex) {
  const group = new THREE.Group();
  group.position.copy(position);
  group.userData.segmentIndex = segmentIndex;
  group.userData.edgeIndex = edgeIndex;
  group.userData.isEdgeHandle = true;
  group.userData.baseColor = HANDLE_COLORS.translate;
  group.userData.hoverColor = HANDLE_COLORS.translateHover;

  const lineGeometry = new BufferGeometry().setFromPoints([
    new Vector3(-0.5, 0, 0),
    new Vector3(0.5, 0, 0),
  ]);
  const lineMaterial = new THREE.LineBasicMaterial({
    color: HANDLE_COLORS.translate,
    transparent: true,
    opacity: EDGE_HANDLE_STYLE.lineOpacity,
    depthTest: false,
  });
  const line = new THREE.Line(lineGeometry, lineMaterial);
  line.raycast = () => {};
  line.renderOrder = 1;

  const blockGeometry = new THREE.BoxGeometry(1, 1, 1);
  const blockMaterial = new THREE.MeshBasicMaterial({
    color: HANDLE_COLORS.translate,
    transparent: true,
    opacity: EDGE_HANDLE_STYLE.blockOpacity,
    depthTest: false,
  });
  const block = new THREE.Mesh(blockGeometry, blockMaterial);
  block.renderOrder = 2;

  group.add(line, block);
  group.userData.edgeLine = line;
  group.userData.edgeBlock = block;

  group.name = "vertex";
  group.renderOrder = 2;

  return group;
}

function createRotationHandle(position, segmentIndex, vertexIndex, baseDir) {
  const handle = new THREE.Group();
  handle.position.copy(position);
  handle.userData.segmentIndex = segmentIndex;
  handle.userData.vertexIndex = vertexIndex;
  handle.userData.isRotationHandle = true;
  handle.userData.baseDir = baseDir.clone();
  handle.userData.baseColor = HANDLE_COLORS.rotate;
  handle.userData.hoverColor = HANDLE_COLORS.rotateHover;

  handle.name = "vertex";
  handle.renderOrder = 2;

  const arcStart = -ROTATION_HANDLE_STYLE.arc * 0.5;
  const geo = new THREE.RingGeometry(
    ROTATION_HANDLE_STYLE.innerRadius,
    ROTATION_HANDLE_STYLE.outerRadius,
    32,
    1,
    arcStart,
    ROTATION_HANDLE_STYLE.arc
  );
  const mat = new THREE.MeshBasicMaterial({
    color: HANDLE_COLORS.rotate,
    transparent: true,
    opacity: 1,
    depthTest: false,
    side: THREE.DoubleSide,
  });
  const ring = new THREE.Mesh(geo, mat);
  ring.renderOrder = 2;
  ring.frustumCulled = false;
  ring.userData.baseOpacity = 1;

  const arrowGeometry = new THREE.ConeGeometry(
    ROTATION_HANDLE_STYLE.arrowSize * 0.65,
    ROTATION_HANDLE_STYLE.arrowSize,
    8
  );
  const arrowMaterial = new THREE.MeshBasicMaterial({
    color: HANDLE_COLORS.rotate,
    transparent: true,
    opacity: 1,
    depthTest: false,
  });
  const makeArrowAtArcEnd = (theta, tangentSign) => {
    const arrow = new THREE.Mesh(arrowGeometry, arrowMaterial.clone());
    const tipRadius = ROTATION_HANDLE_STYLE.outerRadius + ROTATION_HANDLE_STYLE.arrowRadius;
    // Keep arrowhead center exactly on the ring's outer arc radius.
    arrow.position.set(Math.cos(theta) * tipRadius, Math.sin(theta) * tipRadius, 0);

    // Tangent for arc in XY plane at angle theta.
    const tangent = new THREE.Vector3(-Math.sin(theta), Math.cos(theta), 0).multiplyScalar(tangentSign);
    arrow.quaternion.setFromUnitVectors(new THREE.Vector3(0, -1, 0), tangent.normalize());

    arrow.renderOrder = 2;
    arrow.frustumCulled = false;
    arrow.userData.baseOpacity = 1;
    return arrow;
  };

  const startTheta = arcStart;
  const endTheta = ROTATION_HANDLE_STYLE.arc * 0.5;

  const startArrow = makeArrowAtArcEnd(startTheta, +1); // points along increasing theta
  const endArrow = makeArrowAtArcEnd(endTheta, -1); // points along decreasing theta

  handle.add(ring, startArrow, endArrow);
  handle.userData.rotateParts = [ring, startArrow, endArrow];

  return handle;
}

/**
 * Visual highlight for edge hover/drag.
 */
function showEdgeHighlight(segmentIndex, edgeIndex) {
  const segment = POLYGONS[segmentIndex];
  if (!segment) return;
  const edge = segment.edges?.[edgeIndex];
  if (!edge || !edge.point1 || !edge.point2) return;

  const points = [edge.point1.clone(), edge.point2.clone()];
  const newGeometry = new BufferGeometry().setFromPoints(points);

  if (!edgeHighlightLine) {
    const material = new THREE.LineBasicMaterial({
      color: 0x00ff00,
      linewidth: 2,
      transparent: true,
      depthTest: false,
    });
    edgeHighlightLine = new THREE.Line(newGeometry, material);
    edgeHighlightLine.renderOrder = 10;
    edgeHighlightLine.matrixAutoUpdate = true;
    sceneManager.scene.add(edgeHighlightLine);
  } else {
    if (edgeHighlightLine.geometry) edgeHighlightLine.geometry.dispose();
    edgeHighlightLine.geometry = newGeometry;
  }

  edgeHighlightLine.visible = true;
}

function hideEdgeHighlight() {
  if (edgeHighlightLine) {
    edgeHighlightLine.visible = false;
  }
}

function disposeEdgeHighlightLine() {
  if (!edgeHighlightLine) return;
  if (edgeHighlightLine.parent) {
    edgeHighlightLine.parent.remove(edgeHighlightLine);
  }
  if (edgeHighlightLine.geometry) edgeHighlightLine.geometry.dispose();
  if (edgeHighlightLine.material) edgeHighlightLine.material.dispose();
  edgeHighlightLine = null;
}

function createEdgePicker(segmentIndex, edgeIndex, plane, point1, point2) {
  const length = Math.max(point1.distanceTo(point2), 0.001);
  const geometry = new THREE.PlaneGeometry(length, 0.4);
  const material = new THREE.MeshBasicMaterial({
    color: 0x00ff00,
    transparent: true,
    opacity: 0,
    side: THREE.DoubleSide,
    depthTest: false,
  });

  const picker = new THREE.Mesh(geometry, material);
  alignEdgePicker(picker, segmentIndex, edgeIndex, plane, point1, point2);
  return picker;
}

function alignEdgePicker(picker, segmentIndex, edgeIndex, plane, point1, point2) {
  const mid = new Vector3().addVectors(point1, point2).multiplyScalar(0.5);
  picker.position.copy(mid);

  const edgeDir = point2.clone().sub(point1);
  if (edgeDir.lengthSq() > 0) edgeDir.normalize();

  const normal =
    plane && plane.normal && plane.normal.lengthSq() > 0
      ? plane.normal.clone().normalize()
      : new Vector3(0, 0, 1);
  let yAxis = new Vector3().crossVectors(normal, edgeDir).normalize();
  if (yAxis.lengthSq() < 1e-6) {
    yAxis = new Vector3(0, 1, 0);
  }
  const matrix = new THREE.Matrix4().makeBasis(edgeDir, yAxis, normal);
  picker.quaternion.setFromRotationMatrix(matrix);

  picker.userData.segmentIndex = segmentIndex;
  picker.userData.edgeIndex = edgeIndex;
  picker.userData.edgeDir = edgeDir.clone();
  picker.userData.edgeNormal = new Vector3().crossVectors(normal, edgeDir).normalize();
  picker.name = "edgePicker";
  picker.renderOrder = 5;
  picker.frustumCulled = false;
}

function isPointerOverVertex(segmentIndex) {
  const ndc = VARS.mouseNDC;
  if (!ndc) return false;
  const vertices = VERTICES[segmentIndex] || [];
  const handles = edgeHandles.filter((h) => h?.userData?.segmentIndex === segmentIndex);
  const targets = [...vertices, ...handles].filter(Boolean);
  if (!targets.length) return false;
  vertexRaycaster.setFromCamera({ x: ndc.x, y: ndc.y }, sceneManager.camera);
  const intersects = vertexRaycaster.intersectObjects(targets, true);
  return intersects.length > 0;
}

function updateEdgePickers(segmentIndex, coords, plane) {
  if (!edgePickers.length) return;
  const n = coords.length;
  if (edgePickers.length !== n) return;

  for (let i = 0; i < n; i++) {
    const p1 = coords[i];
    const p2 = coords[(i + 1) % n];
    const picker = edgePickers[i];
    if (!picker) continue;
    alignEdgePicker(picker, segmentIndex, i, plane, p1, p2);
  }
}

const onEdgePickerHoverOn = (event) => {
  const { segmentIndex, edgeIndex } = event.object.userData || {};
  if (segmentIndex == null || edgeIndex == null) return;
  if (isPointerOverVertex(segmentIndex)) {
    hideEdgeHighlight();
    return;
  }
  showEdgeHighlight(segmentIndex, edgeIndex);
};

const onEdgePickerHoverOff = () => {
  if (!edgeDragState) {
    hideEdgeHighlight();
  }
};

const onEdgeHandleHoverOn = (event) => {
  const handle = resolveHandleFromEvent(event);
  if (!handle?.userData?.isEdgeHandle) return;
  setHandleHover(handle, true);
};

const onEdgeHandleHoverOff = (event) => {
  const handle = resolveHandleFromEvent(event);
  if (!handle?.userData?.isEdgeHandle) return;
  setHandleHover(handle, false);
};

const onRotationHandleHoverOn = (event) => {
  const handle = resolveHandleFromEvent(event);
  if (!handle?.userData?.isRotationHandle) return;
  setHandleHover(handle, true);
};

const onRotationHandleHoverOff = (event) => {
  const handle = resolveHandleFromEvent(event);
  if (!handle?.userData?.isRotationHandle) return;
  setHandleHover(handle, false);
};

function onEdgePickerDragStart(event) {
  const handle = event.object;
  const { segmentIndex, edgeIndex } = handle.userData;
  if (isPointerOverVertex(segmentIndex)) {
    hideEdgeHighlight();
    return;
  }

  if (currentDragMode && currentDragMode !== "edge") return;
  currentDragMode = "edge";
  VARS.isVertexDragActive = true;
  const segment = POLYGONS[segmentIndex];
  if (!segment) return;

  const plane = createSegmentPlane(segmentIndex);
  if (!plane) return;

  const edge = segment.edges?.[edgeIndex];
  if (!edge) return;
  const p1 = edge.point1.clone();
  const p2 = edge.point2.clone();
  const edgeDir = p2.clone().sub(p1);
  if (edgeDir.lengthSq() < 1e-6) return;
  edgeDir.normalize();

  const normal = plane.normal.clone().normalize();
  const edgeNormal = new Vector3().crossVectors(normal, edgeDir).normalize();
  if (edgeNormal.lengthSq() < 1e-6) return;

  const handleStart = new Vector3();
  plane.projectPoint(handle.position, handleStart);

  edgeDragState = {
    segmentIndex,
    edgeIndex,
    plane,
    edgeNormal,
    handleStart,
    originalPoints: segment.edges.map((e) => e.point1.clone()),
    moved: false,
  };

  if (VARS.segDrag) VARS.segDrag.deactivate();
  if (edgeTranslationDragControls) edgeTranslationDragControls.deactivate();
  if (rotationDragControls) rotationDragControls.deactivate();

  if (sceneManager.controls) sceneManager.controls.enabled = false;
  showEdgeHighlight(segmentIndex, edgeIndex);
}

function onEdgePickerDrag(event) {
  if (currentDragMode !== "edge" || !edgeDragState) return;

  const handle = event.object;
  const {
    segmentIndex,
    edgeIndex,
    plane,
    edgeNormal,
    handleStart,
  } = edgeDragState;

  const projected = new Vector3();
  plane.projectPoint(handle.position, projected);
  handle.position.copy(projected);

  const movement = projected.clone().sub(handleStart);
  const delta = movement.dot(edgeNormal);
  const displacement = edgeNormal.clone().multiplyScalar(delta);

  const segment = POLYGONS[segmentIndex];
  if (!segment) return;

  const coordinates = segment.edges.map((e) => e.point1.clone());
  const vertexCount = coordinates.length;
  const nextIndex = (edgeIndex + 1) % vertexCount;

  const newPoints = coordinates.map((pt, idx) => {
    if (idx === edgeIndex || idx === nextIndex) {
      return pt.clone().add(displacement);
    }
    return pt.clone();
  });

  applySegmentCoordinates(segmentIndex, newPoints);
  updateSegmentMeasurementLabels(segmentIndex);
  updateDetails(segment.shape.uuid, currentSetPolygonDetails);
  showEdgeHighlight(segmentIndex, edgeIndex);

  edgeDragState.handleStart.copy(projected);

  if (Math.abs(delta) > 1e-6) {
    edgeDragState.moved = true;
  }
}

function onEdgePickerDragEnd(event) {
  if (currentDragMode !== "edge" || !edgeDragState) return;

  const { segmentIndex, originalPoints, moved } = edgeDragState;

  currentDragMode = null;
  edgeDragState = null;
  VARS.isVertexDragActive = false;

  if (sceneManager.controls) sceneManager.controls.enabled = true;
  if (VARS.segDrag) VARS.segDrag.activate();
  if (edgeTranslationDragControls) edgeTranslationDragControls.activate();
  if (rotationDragControls) rotationDragControls.activate();
  if (edgeDragControls) edgeDragControls.activate();

  hideEdgeHighlight();

  if (moved) {
    const finalPoints = getSegmentCurrentPoints(segmentIndex);
    pushUndoAction({
      label: "Move edge",
      undo: () => applySegmentCoordinates(segmentIndex, originalPoints),
      redo: () => applySegmentCoordinates(segmentIndex, finalPoints),
    });
    dispatchSegmentGeometryChanged(segmentIndex);
  }
}

/**
 * Update all measurement labels for a segment after geometry change.
 */
function updateSegmentMeasurementLabels(segmentIndex) {
  const segment = POLYGONS[segmentIndex];
  if (!segment || !Array.isArray(segment.edges)) return;

  for (const edge of segment.edges) {
    const { point1, point2, id } = edge;
    if (!point1 || !point2 || !id) continue;

    const labelObj = LABELS.measurements[id];
    if (!labelObj) continue;

    const distanceInMeters = point1.distanceTo(point2);
    const { totalDistanceInFeet, text } = formatFeetAndInches(distanceInMeters);

    const currentText = labelObj.element.innerText || "";
    const parts = currentText.split(":");
    let prefix = "";
    if (parts.length > 1) {
      prefix = parts[0] + ": ";
    }

    labelObj.element.innerText = `${prefix}${text}`;
    labelObj.position.lerpVectors(point1, point2, 0.5);
    edge.measurement = totalDistanceInFeet;
  }
}

/**
 * Apply a new set of polygon vertices (world-space) to a segment.
 */
function applySegmentCoordinates(segmentIndex, newPoints) {
  const segment = POLYGONS[segmentIndex];
  if (!segment || !segment.shape || !newPoints || !newPoints.length) return;

  const segmentMesh = segment.shape;
  const geom = segmentMesh.geometry;
  const positionAttr = geom.attributes.position;
  const vertexCount = positionAttr.count;

  if (vertexCount !== newPoints.length) {
    console.warn(
      "applySegmentCoordinates: vertex count mismatch",
      vertexCount,
      newPoints.length
    );
  }

  const n = Math.min(vertexCount, newPoints.length);

  // 1) mesh geometry
  for (let i = 0; i < n; i++) {
    const p = newPoints[i];
    positionAttr.setXYZ(i, p.x, p.y, p.z);
  }
  positionAttr.needsUpdate = true;
  geom.computeVertexNormals();
  geom.computeBoundingSphere();

  // 2) VERTICES spheres
  if (VERTICES[segmentIndex]) {
    const verts = VERTICES[segmentIndex];
    for (let i = 0; i < Math.min(verts.length, n); i++) {
      verts[i].position.copy(newPoints[i]);
    }
  }

  // 3) wireframe
  const coordinates = [];
  for (let i = 0; i < n; i++) {
    coordinates.push(newPoints[i].clone());
  }

  const edgePoints = [];
  for (let i = 0; i < coordinates.length; i++) {
    const current = coordinates[i];
    const nextIdx = (i + 1) % coordinates.length;
    const nextPt = coordinates[nextIdx];
    edgePoints.push(current, nextPt);
  }

  const wireframe = segmentMesh.children.slice(-1)[0];
  if (wireframe) {
    updateSegmentWireframe(wireframe, edgePoints);
  }

  // 4) update POLYGONS[*].edges
  const edges = segment.edges;
  for (let i = 0; i < coordinates.length && i < edges.length; i++) {
    const p1 = coordinates[i];
    const p2 = coordinates[(i + 1) % coordinates.length];

    if (!edges[i].point1) edges[i].point1 = p1.clone();
    else edges[i].point1.copy(p1);

    if (!edges[i].point2) edges[i].point2 = p2.clone();
    else edges[i].point2.copy(p2);
  }

  // 5) measurement labels
  updateSegmentMeasurementLabels(segmentIndex);

  // 6) recenter name label
  const center = getPointsCenter(coordinates);
  if (segment.label) {
    segment.label.position.copy(center);
  }

  // 7) update UI details
  if (currentSetPolygonDetails) {
    updateDetails(segmentMesh.uuid, currentSetPolygonDetails);
  }

  // 8) reposition transform handles
  updateTransformHandlesForSegment(segmentIndex, coordinates);

  // 9) keep setbacks synced with segment geometry after any transform/edit
  syncSetbacksAfterGeometryChange(segmentIndex);
}

function syncSetbacksAfterGeometryChange(segmentIndex) {
  const segment = POLYGONS[segmentIndex];
  const edges = segment?.edges;
  const setBacks = segment?.setBacks;
  if (!segment || !Array.isArray(edges) || !Array.isArray(setBacks?.edgesData)) return;

  const prev = setBacks.edgesData;
  const values = edges.map((_, i) => Math.max(0, Number(prev?.[i]?.value) || 0));
  const hasActiveSetbacks = values.some((value) => value > 0);

  // Rebase setback edges to latest polygon edges, preserving per-edge values.
  const next = edges.map((edge, i) => ({
    vertexId1: edge.vertexId1 ?? prev?.[i]?.vertexId1 ?? null,
    vertexId2: edge.vertexId2 ?? prev?.[i]?.vertexId2 ?? null,
    value: values[i],
    point1: edge.point1.clone(),
    point2: edge.point2.clone(),
  }));
  setBacks.edgesData = next;
  setBacks.isSet = hasActiveSetbacks;

  if (!hasActiveSetbacks) return;

  for (let i = 0; i < next.length; i++) {
    const value = values[i];
    if (value > 0) {
      addSetbackToEdge(next, i, value, segmentIndex);
    }
  }
  updateSetbackWireframe(next, segmentIndex);
}

/**
 * Update transform handles positions for a segment.
 */
function updateTransformHandlesForSegment(segmentIndex, coordinates) {
  const segment = POLYGONS[segmentIndex];
  if (!segment) return;

  const n = coordinates.length;
  if (n === 0) return;

  if (edgeHandles.length !== n) return;

  const center = getPointsCenter(coordinates);
  const plane = createSegmentPlane(segmentIndex);
  const planeNormal = getPlaneNormal(plane);

  // edge handles (midpoints)
  for (let i = 0; i < n; i++) {
    const p1 = coordinates[i];
    const p2 = coordinates[(i + 1) % n];
    updateEdgeHandleVisual(edgeHandles[i], p1, p2, planeNormal);
  }

  // rotation handle(s): only update the handles that exist, anchored to the
  // vertex index each handle was created for.
  for (const handle of rotationHandles) {
    const vi = handle.userData.vertexIndex;
    if (vi == null || vi >= n) continue;
    const corner = coordinates[vi];
    updateRotationHandleVisual(handle, center, corner, planeNormal);
  }
  updateEdgePickers(segmentIndex, coordinates, plane);
}

/**
 * Tear down and dispose all transform handles & DragControls.
 */
function clearTransformHandles() {
  if (edgeTranslationDragControls) {
    edgeTranslationDragControls.deactivate();
    edgeTranslationDragControls.dispose();
    edgeTranslationDragControls = null;
  }
  if (edgeDragControls) {
    edgeDragControls.deactivate();
    edgeDragControls.dispose();
    edgeDragControls = null;
  }
  if (rotationDragControls) {
    rotationDragControls.deactivate();
    rotationDragControls.dispose();
    rotationDragControls = null;
  }

  edgeHandles.forEach((h) => disposeObject3D(h));
  rotationHandles.forEach((h) => disposeObject3D(h));
  edgePickers.forEach((p) => disposeObject3D(p));

  edgeHandles = [];
  rotationHandles = [];
  edgePickers = [];
  translateState = null;
  rotationState = null;
  edgeDragState = null;
  hideEdgeHighlight();
  disposeEdgeHighlightLine();
  currentDragMode = null;
  VARS.isVertexDragActive = false;
}

/**
 * Logs the live pitch/azimuth degrees of a segment. Diagnostic only -- a logging
 * failure (e.g. no project CRS in dev) must never break selection or dragging.
 */
function logSegmentAngles(index, reason) {
  try {
    const segment = POLYGONS[index];
    const lines = segment?.edges;
    if (!lines || lines.length < 3) {
      console.log("Drawing", { segment: index, reason, pitch: null, azimuth: null });
      return;
    }

    const res = Details.calculatePitch(lines);
    let azimuth = null;
    try {
      azimuth = `${Details.getAzimuth(res.p2, res.p1).toFixed(2)}°`;
    } catch (e) {
      azimuth = "n/a (no project CRS)";
    }

    console.log("Drawing", {
      segment: index,
      reason,
      vertices: lines.length,
      pitch: `${res.pitch_angle}°`,
      azimuth,
      area: Details.getPolyArea(lines).toFixed(2),
    });
  } catch (e) {
    console.warn("Drawing — angle log failed:", e);
  }
}

/**
 * Enables drag controls for a polygon's vertices + whole-segment transform handles.
 */
export const setSegmentDragControls = (index, setPolygonDetails) => {
  currentSetPolygonDetails = setPolygonDetails || null;

  // Remove any existing drag controls + transform handles
  removeSegmentDragControls();
  logSegmentAngles(index, "attach drag controls");

  VARS.segDrag = new DragControls(
    VERTICES[index],
    sceneManager.camera,
    sceneManager.renderer.domElement
  );

  VARS.segDrag.addEventListener("hoveron", dragHoverOn);
  VARS.segDrag.addEventListener("hoveroff", dragHoverOff);
  VARS.segDrag.addEventListener("dragend", (event) => dragEnd(event));
  VARS.segDrag.addEventListener("dragstart", (event) => dragStart(event));
  VARS.segDrag.addEventListener("drag", (event) => onDrag(event, setPolygonDetails));

  setupTransformHandlesForSegment(index);

  return VARS.segDrag;
};

function setupTransformHandlesForSegment(segmentIndex) {
  clearTransformHandles();

  const segment = POLYGONS[segmentIndex];
  if (!segment || !segment.shape || !segment.edges || !segment.edges.length) return;

  const scene = sceneManager.scene;
  const camera = sceneManager.camera;
  const domElement = sceneManager.renderer.domElement;

  const n = segment.edges.length;
  if (n === 0) return;

  const coords = [];
  for (let i = 0; i < n; i++) {
    const p1 = segment.edges[i].point1;
    coords.push(p1.clone());
  }

  const center = getPointsCenter(coords);
  const plane = createSegmentPlane(segmentIndex);
  const planeNormal = getPlaneNormal(plane);

  edgeHandles = [];
  for (let i = 0; i < n; i++) {
    const p1 = coords[i];
    const p2 = coords[(i + 1) % n];
    const mid = new Vector3().addVectors(p1, p2).multiplyScalar(0.5);
    const handle = createEdgeHandle(mid, segmentIndex, i);
    updateEdgeHandleVisual(handle, p1, p2, planeNormal);
    scene.add(handle);
    edgeHandles.push(handle);
  }

  rotationHandles = [];
  // Show a single rotation handle (at the first vertex). Dragging it still
  // rotates the whole segment around its center.
  if (n > 0) {
    const i = 0;
    const corner = coords[i];
    const handle = createRotationHandle(corner, segmentIndex, i, corner.clone().sub(center));
    updateRotationHandleVisual(handle, center, corner, planeNormal);
    scene.add(handle);
    rotationHandles.push(handle);
  }

  edgeTranslationDragControls = new DragControls(edgeHandles, camera, domElement);
  edgeTranslationDragControls.transformGroup = true;
  edgeTranslationDragControls.addEventListener("hoveron", onEdgeHandleHoverOn);
  edgeTranslationDragControls.addEventListener("hoveroff", onEdgeHandleHoverOff);
  edgeTranslationDragControls.addEventListener("dragstart", onEdgeDragStart);
  edgeTranslationDragControls.addEventListener("drag", onEdgeDrag);
  edgeTranslationDragControls.addEventListener("dragend", onEdgeDragEnd);

  rotationDragControls = new DragControls(rotationHandles, camera, domElement);
  rotationDragControls.transformGroup = true;
  rotationDragControls.addEventListener("hoveron", onRotationHandleHoverOn);
  rotationDragControls.addEventListener("hoveroff", onRotationHandleHoverOff);
  rotationDragControls.addEventListener("dragstart", onRotationDragStart);
  rotationDragControls.addEventListener("drag", onRotationDrag);
  rotationDragControls.addEventListener("dragend", onRotationDragEnd);

  edgePickers = [];
  for (let i = 0; i < n; i++) {
    const p1 = coords[i];
    const p2 = coords[(i + 1) % n];
    const picker = createEdgePicker(segmentIndex, i, plane, p1, p2);
    scene.add(picker);
    edgePickers.push(picker);
  }

  edgeDragControls = new DragControls(edgePickers, camera, domElement);
  edgeDragControls.addEventListener("hoveron", onEdgePickerHoverOn);
  edgeDragControls.addEventListener("hoveroff", onEdgePickerHoverOff);
  edgeDragControls.addEventListener("dragstart", onEdgePickerDragStart);
  edgeDragControls.addEventListener("drag", onEdgePickerDrag);
  edgeDragControls.addEventListener("dragend", onEdgePickerDragEnd);
}

/**
 * Disposes any existing drag controls and transform handles.
 */
export const removeSegmentDragControls = () => {
  if (VARS.segDrag) {
    VARS.segDrag.dispose();

    VARS.segDrag.removeEventListener("hoveron", dragHoverOn);
    VARS.segDrag.removeEventListener("hoveroff", dragHoverOff);
    VARS.segDrag.removeEventListener("dragend", dragEnd);
    VARS.segDrag.removeEventListener("dragstart", dragStart);
    VARS.segDrag.removeEventListener("drag", onDrag);

    VARS.segDrag = null;
  }

  clearTransformHandles();
  currentDragMode = null;
};

// -------------------------------------------------------
// Existing vertex drag handlers + vertex undo/redo
// -------------------------------------------------------

const dragHoverOn = (event) => {
  event.object.material.opacity = 1;
};

const dragHoverOff = (event) => {
  event.object.material.opacity = 1;
};

const dragStart = (event) => {
  if (currentDragMode && currentDragMode !== "vertex") return;
  currentDragMode = "vertex";

  const vertexId = event.object.uuid;
  const segmentMesh = event.object.parent;

  for (let i in segmentMesh.children) {
    if (segmentMesh.children[i].uuid === vertexId) {
      vertexIndex = Number(i);
    }
  }

  // Find segment index in POLYGONS
  const segmentIndex = POLYGONS.findIndex(
    (seg) => seg.shape.uuid === segmentMesh.uuid
  );

  if (segmentIndex !== -1) {
    const segment = POLYGONS[segmentIndex];
    const originalPoints = segment.edges.map((e) => e.point1.clone());
    // Capture the segment's flat plane (local space) while the geometry is
    // still planar, so dragged vertices stay on the boundary plane instead of
    // popping onto the bumpy model surface.
    const segmentPlaneLocal = createSegmentPlane(segmentIndex);
    // Same plane in world space. The drag ray is intersected against this instead
    // of raycasting the model, so the vertex tracks the cursor identically whether
    // the pointer is inside the segment or out over the ground -- an infinite plane
    // cannot be "missed" and cannot swap to another surface mid-gesture.
    const segmentPlaneWorld = createSegmentPlaneWorld(segmentMesh);
    vertexDragState = {
      segmentIndex,
      originalPoints,
      segmentPlaneLocal,
      segmentPlaneWorld,
      didMove: false,
    };
  } else {
    vertexDragState = null;
  }

  currentWireframe = segmentMesh.children.slice(-1)[0] || null;
  if (currentWireframe && currentWireframe.material) {
    wireframeBaseColor = currentWireframe.material.color.clone();
  } else {
    wireframeBaseColor = null;
  }

  VARS.isVertexDragActive = true;
  sceneManager.controls.enabled = false;

  ensureAngleLabelDiv();
  angleLabelDiv.style.display = "block";
};

const dragEnd = (event) => {
  if (currentWireframe && wireframeBaseColor) {
    currentWireframe.material.color.copy(wireframeBaseColor);
  }
  currentWireframe = null;
  wireframeBaseColor = null;

  sceneManager.controls.enabled = true;
  VARS.isVertexDragActive = false;

  hideAngleLabel();

  // push undo/redo for vertex move if there was movement
  if (vertexDragState && vertexDragState.didMove) {
    const { segmentIndex, originalPoints } = vertexDragState;
    const finalPoints = getSegmentCurrentPoints(segmentIndex);
    pushUndoAction({
      label: "Move vertex",
      undo: () => {
        applySegmentCoordinates(segmentIndex, originalPoints);
      },
      redo: () => {
        applySegmentCoordinates(segmentIndex, finalPoints);
      },
    });
    dispatchSegmentGeometryChanged(segmentIndex);
  }
  vertexDragState = null;

  if (currentDragMode === "vertex") {
    currentDragMode = null;
  }
};

/**
 * Builds the segment's plane in world space from its first 3 geometry vertices.
 * Returns null if those points are degenerate (collinear/coincident).
 */
function createSegmentPlaneWorld(segmentMesh) {
  const gp = segmentMesh?.geometry?.attributes?.position;
  if (!gp || gp.count < 3) return null;

  segmentMesh.updateWorldMatrix(true, false);
  const p1 = new Vector3().fromBufferAttribute(gp, 0).applyMatrix4(segmentMesh.matrixWorld);
  const p2 = new Vector3().fromBufferAttribute(gp, 1).applyMatrix4(segmentMesh.matrixWorld);
  const p3 = new Vector3().fromBufferAttribute(gp, 2).applyMatrix4(segmentMesh.matrixWorld);

  const plane = new THREE.Plane().setFromCoplanarPoints(p1, p2, p3);
  return plane.normal.lengthSq() > 0 ? plane : null;
}

/**
 * Intersects the mouse ray with the segment's own world-space plane.
 * Returns null when there is no plane or the ray is parallel to it (edge-on view).
 */
function getRaycastHitOnSegmentPlane() {
  const ndc = VARS.mouseNDC;
  const plane = vertexDragState?.segmentPlaneWorld;
  if (!ndc || !plane) return null;

  const raycaster = new Raycaster();
  raycaster.setFromCamera({ x: ndc.x, y: ndc.y }, sceneManager.camera);

  const hit = new Vector3();
  return raycaster.ray.intersectPlane(plane, hit) ? hit : null;
}

/**
 * Raycast from mouse NDC to model, returns world-space hit.
 */
function getRaycastHitOnModel() {
  const ndc = VARS.mouseNDC;
  const meshes = VARS.Meshes || [];

  if (!ndc || !meshes.length) return null;

  const raycaster = new Raycaster();
  raycaster.setFromCamera({ x: ndc.x, y: ndc.y }, sceneManager.camera);

  const intersects = raycaster.intersectObjects(meshes, true);
  if (!intersects.length) {
    logDragRayTarget("<no hit — falling back to camera-plane position>", null);
    return null;
  }

 // logDragRayTarget(
   // intersects[0].object?.name || intersects[0].object?.uuid || "<unnamed>",
   // intersects[0].point
 // );
  return intersects[0].point.clone();
}

// The dragged vertex snaps to whatever model surface is under the cursor, so a
// mid-drag change of target (roof -> ground/wall/other face) is what makes the
// vertex jump in a direction the mouse never moved. Log the target on change only.
let lastDragRayTarget = null;
function logDragRayTarget(targetName, point) {
  if (targetName === lastDragRayTarget) return;
  lastDragRayTarget = targetName;
 // console.log("Drawing — drag ray target changed", {
   // hit: targetName,
  //  z: point ? Number(point.z.toFixed(3)) : null,
 // });
}

const onDrag = (event, setPolygonDetails) => {
  const segmentMesh = event.object.parent;
  const positionAttr = segmentMesh.geometry.attributes.position;
  const vertexCount = positionAttr.count;

  if (typeof vertexIndex !== "number" || vertexCount < 3) {
    return;
  }

  // Prefer the segment's own plane. Raycasting the model made the vertex snap to
  // whatever surface was under the cursor, so leaving the roof face swapped the hit
  // to the ground metres below and threw the vertex off in an unrelated direction.
  // The plane keeps the motion continuous inside and outside the segment alike.
  const hitWorld = getRaycastHitOnSegmentPlane() || getRaycastHitOnModel();
  let localPos;
  if (hitWorld) {
    localPos = segmentMesh.worldToLocal(hitWorld.clone());
  } else {
    localPos = event.object.position.clone();
  }

  // Keep the dragged vertex on the segment's flat plane so the moving edge
  // stays aligned with the rest of the boundary (matches edge-drag behavior).
  const segmentPlaneLocal = vertexDragState?.segmentPlaneLocal;
  if (segmentPlaneLocal) {
    segmentPlaneLocal.projectPoint(localPos, localPos);
  }

  let snappedPosLocal = localPos.clone();
  let angleDeg = null;

  const prevIndex = (vertexIndex - 1 + vertexCount) % vertexCount;
  const nextIndex = (vertexIndex + 1) % vertexCount;

  const prev = new Vector3(
    positionAttr.getX(prevIndex),
    positionAttr.getY(prevIndex),
    positionAttr.getZ(prevIndex)
  );
  const next = new Vector3(
    positionAttr.getX(nextIndex),
    positionAttr.getY(nextIndex),
    positionAttr.getZ(nextIndex)
  );

  const angleBeforeSnap = computeAngleDeg(prev, localPos, next);
  angleDeg = angleBeforeSnap;

  if (
    angleBeforeSnap != null &&
    Math.abs(angleBeforeSnap - 90) <= ANGLE_SNAP_ENTER_TOL_DEG
  ) {
    const snapCandidate = getVertexRightAngleSnap(
      prev,
      next,
      localPos,
      VERTEX_90_SNAP_DIST
    );
    if (snapCandidate) {
      snappedPosLocal.copy(snapCandidate);
    }
  }

  event.object.position.copy(snappedPosLocal);

  const centerLocal = snappedPosLocal;
  const centerWorld = segmentMesh.localToWorld(centerLocal.clone());

  positionAttr.setXYZ(vertexIndex, centerLocal.x, centerLocal.y, centerLocal.z);
  positionAttr.needsUpdate = true;
  segmentMesh.geometry.computeVertexNormals();
  segmentMesh.geometry.computeBoundingSphere();

  const coordinates = [];
  for (let i = 0; i < positionAttr.count; i++) {
    coordinates.push(
      new Vector3(
        positionAttr.getX(i),
        positionAttr.getY(i),
        positionAttr.getZ(i)
      )
    );
  }

  const edgePoints = [];
  for (let i = 0; i < coordinates.length; i++) {
    const current = coordinates[i];
    const nextIdx = (i + 1) % coordinates.length;
    const nextPt = coordinates[nextIdx];
    edgePoints.push(current, nextPt);
  }

  const wireframe = segmentMesh.children.slice(-1)[0];
  if (wireframe) {
    updateSegmentWireframe(wireframe, edgePoints);
  }

  const A = coordinates[prevIndex];
  const B = coordinates[nextIndex];
  const angleAfter = computeAngleDeg(A, centerLocal, B);
  if (angleAfter != null) {
    angleDeg = angleAfter;
  }

  const segmentIndex = POLYGONS.findIndex(
    (seg) => seg.shape.uuid === segmentMesh.uuid
  );
  if (segmentIndex !== -1) {
    const segment = POLYGONS[segmentIndex];
    const edges = segment.edges;
    const n = coordinates.length;

    for (let i = 0; i < n; i++) {
      const p1 = coordinates[i];
      const p2 = coordinates[(i + 1) % n];

      if (!edges[i].point1) edges[i].point1 = p1.clone();
      else edges[i].point1.copy(p1);

      if (!edges[i].point2) edges[i].point2 = p2.clone();
      else edges[i].point2.copy(p2);
    }

    updateTransformHandlesForSegment(segmentIndex, coordinates);
    syncSetbacksAfterGeometryChange(segmentIndex);
  }

  const isReallyRightAngle =
    angleDeg != null && Math.abs(angleDeg - 90) <= ANGLE_HIGHLIGHT_TOL_DEG;

  if (isReallyRightAngle && wireframe && wireframe.material) {
    wireframe.material.color.setHex(0x00ff00);
  } else if (wireframe && wireframe.material && wireframeBaseColor) {
    wireframe.material.color.copy(wireframeBaseColor);
  }

  if (angleDeg != null) {
    updateAngleLabel(angleDeg, centerWorld);
  }

  updateLabelPositions(event, centerWorld);
  updateDetails(segmentMesh.uuid, setPolygonDetails);

  // mark that something actually moved for vertex-undo
  if (vertexDragState) {
    vertexDragState.didMove = true;
  }
};

// -------------------------------------------------------
// Segment-level translation via edge handles + undo/redo
// -------------------------------------------------------

function onEdgeDragStart(event) {
  VARS.isVertexDragActive = true;
  if (currentDragMode && currentDragMode !== "translate") return;
  currentDragMode = "translate";

  const handle = resolveHandleFromEvent(event);
  if (!handle) return;
  const segmentIndex = handle.userData.segmentIndex;
  const segment = POLYGONS[segmentIndex];
  if (!segment) return;

  const plane = createSegmentPlane(segmentIndex);
  if (!plane) return;

  const originalPoints = segment.edges.map((e) => e.point1.clone());
  const handleStart = new Vector3();
  plane.projectPoint(handle.position, handleStart);

  // The edge this handle controls connects vertex edgeIndex -> edgeIndex+1.
  const n = originalPoints.length;
  const edgeIndex = handle.userData.edgeIndex;
  const p1 = originalPoints[edgeIndex];
  const p2 = originalPoints[(edgeIndex + 1) % n];

  // In-plane normal of the edge (perpendicular to the edge, lying on the
  // segment plane). Dragging along this resizes the segment: inward shrinks,
  // outward grows.
  const edgeDir = p2.clone().sub(p1).normalize();
  const planeNormal = plane.normal.clone().normalize();
  const edgeNormal = planeNormal.clone().cross(edgeDir).normalize();

  translateState = {
    segmentIndex,
    plane,
    originalPoints,
    handleStart,
    edgeIndex,
    edgeNormal,
    moved: false, // ⬅️ track if user actually translated
  };

  if (VARS.segDrag) VARS.segDrag.deactivate();
  if (rotationDragControls) rotationDragControls.deactivate();
  if (edgeDragControls) edgeDragControls.deactivate();
  hideEdgeHighlight();

  if (sceneManager.controls) sceneManager.controls.enabled = false;
}

function onEdgeDrag(event) {
  if (currentDragMode !== "translate" || !translateState) return;

  const { segmentIndex, plane, originalPoints, handleStart, edgeIndex, edgeNormal } =
    translateState;
  const segment = POLYGONS[segmentIndex];
  if (!segment) return;

  const handle = resolveHandleFromEvent(event);
  if (!handle) return;

  const projected = new Vector3();
  plane.projectPoint(handle.position, projected);

  const delta = projected.clone().sub(handleStart);

  // Move only the two vertices of the dragged edge, constrained to the edge's
  // perpendicular so the edge slides in/out (resizing the segment) instead of
  // translating the whole shape.
  const dist = delta.dot(edgeNormal);
  const move = edgeNormal.clone().multiplyScalar(dist);

  const n = originalPoints.length;
  const newPoints = originalPoints.map((p) => p.clone());
  newPoints[edgeIndex].add(move);
  newPoints[(edgeIndex + 1) % n].add(move);
  applySegmentCoordinates(segmentIndex, newPoints);

  // Keep the handle glued to the (moved) edge midpoint.
  const newMid = newPoints[edgeIndex]
    .clone()
    .add(newPoints[(edgeIndex + 1) % n])
    .multiplyScalar(0.5);
  handle.position.copy(newMid);

  // mark as moved if the edge actually moved
  if (Math.abs(dist) > 1e-5) {
    translateState.moved = true;
  }
}

function onEdgeDragEnd(event) {
  VARS.isVertexDragActive = false;
  if (currentDragMode !== "translate" || !translateState) {
    return;
  }

  const { segmentIndex, originalPoints, moved } = translateState;

  currentDragMode = null;
  translateState = null;

  if (sceneManager.controls) sceneManager.controls.enabled = true;

  if (VARS.segDrag) VARS.segDrag.activate();
  setupTransformHandlesForSegment(segmentIndex);

  // push undo/redo if there was an actual translation
  if (moved) {
    const finalPoints = getSegmentCurrentPoints(segmentIndex);
    pushUndoAction({
      label: "Resize segment edge",
      undo: () => {
        applySegmentCoordinates(segmentIndex, originalPoints);
      },
      redo: () => {
        applySegmentCoordinates(segmentIndex, finalPoints);
      },
    });
    dispatchSegmentGeometryChanged(segmentIndex);
  }
}

// -------------------------------------------------------
// Segment-level rotation via rotation handles + undo/redo
// -------------------------------------------------------

function onRotationDragStart(event) {
  VARS.isVertexDragActive = true;
  if (currentDragMode && currentDragMode !== "rotate") return;
  currentDragMode = "rotate";

  const handle = event.object;
  const segmentIndex = handle.userData.segmentIndex;
  const segment = POLYGONS[segmentIndex];
  if (!segment) return;

  const plane = createSegmentPlane(segmentIndex);
  if (!plane) return;

  const originalPoints = segment.edges.map((e) => e.point1.clone());
  const center = getPointsCenter(originalPoints);
  const baseDir = handle.userData.baseDir.clone();

  rotationState = {
    segmentIndex,
    plane,
    center,
    originalPoints,
    baseDir,
    moved: false, // ⬅️ track if rotated
  };

  if (VARS.segDrag) VARS.segDrag.deactivate();
  if (edgeTranslationDragControls) edgeTranslationDragControls.deactivate();
  if (edgeDragControls) edgeDragControls.deactivate();
  hideEdgeHighlight();

  if (sceneManager.controls) sceneManager.controls.enabled = false;
}

function onRotationDrag(event) {
  if (currentDragMode !== "rotate" || !rotationState) return;

  const { segmentIndex, plane, center, originalPoints, baseDir } = rotationState;
  const segment = POLYGONS[segmentIndex];
  if (!segment) return;

  const handle = event.object;

  const projected = new Vector3();
  plane.projectPoint(handle.position, projected);
  handle.position.copy(projected);

  const currentDir = projected.clone().sub(center);
  if (currentDir.lengthSq() < 1e-8) return;

  const normal = plane.normal.clone().normalize();
  const angle = signedAngleBetween(baseDir, currentDir, normal);

  const newPoints = [];
  for (let i = 0; i < originalPoints.length; i++) {
    const v = originalPoints[i].clone().sub(center);
    v.applyAxisAngle(normal, angle);
    const rotated = center.clone().add(v);
    newPoints.push(rotated);
  }

  applySegmentCoordinates(segmentIndex, newPoints);

  if (Math.abs(angle) > 1e-4) {
    rotationState.moved = true;
  }
}

function onRotationDragEnd(event) {
  if (currentDragMode !== "rotate" || !rotationState) return;

  const { segmentIndex, originalPoints, moved } = rotationState;

  currentDragMode = null;
  rotationState = null;

  VARS.isVertexDragActive = false;
  if (sceneManager.controls) sceneManager.controls.enabled = true;

  if (VARS.segDrag) VARS.segDrag.activate();
  if (edgeTranslationDragControls) edgeTranslationDragControls.activate();
  if (edgeDragControls) edgeDragControls.activate();

  // push undo/redo if rotated
  if (moved) {
    const finalPoints = getSegmentCurrentPoints(segmentIndex);
    pushUndoAction({
      label: "Rotate segment",
      undo: () => {
        applySegmentCoordinates(segmentIndex, originalPoints);
      },
      redo: () => {
        applySegmentCoordinates(segmentIndex, finalPoints);
      },
    });
    dispatchSegmentGeometryChanged(segmentIndex);
  }
}

// -------------------------------------------------------
// Area/pitch/azimuth update helper
// -------------------------------------------------------

// Last pitch|azimuth logged per segment, so the drag log fires on change only.
const lastLoggedAngleStamp = new Map();

function updateDetails(parentId, setPolygonDetails) {
  for (let segment of POLYGONS) {
    if (segment.shape.uuid === parentId) {
      const lines = segment.edges;

      const res = Details.calculatePitch(lines);
      const pitch = res.pitch_angle;
      const area = Details.getPolyArea(lines).toFixed(2);
      const azimu = Details.getAzimuth(res.p2, res.p1).toFixed(2);

      const data = { pitch, area, azimu };

      // Live degrees while dragging. updateDetails runs every drag frame, so log
      // only when the angle actually changes -- otherwise this floods the console.
      const stamp = `${pitch}|${azimu}`;
      if (lastLoggedAngleStamp.get(parentId) !== stamp) {
        lastLoggedAngleStamp.set(parentId, stamp);
        //console.log("Drawing — drag", { pitch: `${pitch}°`, azimuth: `${azimu}°`, area });
      }

      setPolygonDetails(data);
      segment.details = data;
      break;
    }
  }
}
